import { useEffect, useRef, useState, type FormEvent, type ReactNode } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { clearSession, getCurrentUser, getSession, setSession } from "../lib/auth";
import {
  getBranding,
  getMe,
  getWorkspaceConfig,
  logout as apiLogout,
  updateMyProfile,
  listTicketTemplates,
  updateTicketTemplate,
  deleteTicketTemplate,
} from "../lib/api";
import type { TicketTemplate } from "../lib/types";
import { applyBranding } from "../lib/branding";
import { useApi, useMutation } from "../lib/useApi";
import { orgFromHost, BRAND } from "../lib/format";
import { TicketNumberingProvider } from "../lib/ticketNumbering";
import { FacilityNumberingProvider } from "../lib/facilityNumbering";
import { ChangeNumberingProvider } from "../lib/changeNumbering";
import { GlobalSearch } from "./GlobalSearch";
import { NotificationsBell } from "./NotificationsBell";
import { PullToRefresh } from "./PullToRefresh";
import { ThemeToggle } from "./ThemeToggle";
import { Modal } from "./Modal";
import { Alert, Spinner } from "./States";
import { Icon, type IconName } from "./Icon";
import { Logo } from "./Logo";
import { StatusColorStyles } from "./StatusColorStyles";

/** Sidebar modules, grouped into logical sections. Each item is gated by a
 *  permission code (null = every user); a whole section hides when all its
 *  items are hidden. Mirrors the server guards — hiding is UX, the API enforces.
 *
 *  Sections:
 *   • Work       — the daily queue + your own work
 *   • Insights   — cross-cutting reporting & objectives
 *   • Configure  — content + automation + tenant administration */
type NavItem = { to: string; label: string; icon: IconName; perm: string | null; feature?: string };
const NAV_SECTIONS: { title: string; items: NavItem[] }[] = [
  {
    title: "Work",
    items: [
      // My Work first — the personal landing for everyone (welcome + workload).
      { to: "/my-work", label: "My Work", icon: "my-work", perm: null },
      { to: "/tickets", label: "Tickets", icon: "tickets", perm: "ticket.read" },
      { to: "/projects", label: "Projects", icon: "projects", perm: "project.read" },
      // Action Items are private (creator + assignees); visible to everyone, the
      // server enforces per-item visibility.
      { to: "/action-items", label: "Action Items", icon: "action-items", perm: null },
      // Change Management (work type "change_management") — operational/IT changes.
      { to: "/change-management", label: "Change Management", icon: "action-items", perm: "change.read" },
      { to: "/access-requests", label: "Approvals", icon: "access", perm: "project.read" },
      { to: "/kb", label: "Knowledge Base", icon: "kb", perm: "kb.read" },
    ],
  },
  {
    title: "Assets",
    items: [
      { to: "/assets", label: "Inventory", icon: "assets", perm: "asset.read" },
      { to: "/assets/assignments", label: "Assignments", icon: "user", perm: "asset.read" },
    ],
  },
  {
    title: "Facilities",
    items: [
      { to: "/facilities", label: "Work Orders", icon: "wrench", perm: "facility.read" },
      { to: "/facilities/assets", label: "Equipment", icon: "equipment", perm: "facility.read" },
      { to: "/facilities/parts", label: "Parts", icon: "parts", perm: "facility.read" },
      { to: "/facilities/chat", label: "Team Chat", icon: "wrench", perm: "facility.read" },
    ],
  },
  {
    title: "Insights",
    items: [
      { to: "/dashboards", label: "Dashboards", icon: "dashboards", perm: "dashboard.read" },
      { to: "/reports", label: "Reports", icon: "reports", perm: "report.read" },
      { to: "/goals", label: "Goals", icon: "target", perm: "goal.read" },
    ],
  },
  {
    title: "Configure",
    items: [
      { to: "/forms", label: "Forms", icon: "forms", perm: "form.admin" },
      { to: "/automations", label: "Automations", icon: "bell", perm: "automation.admin", feature: "automations" },
      { to: "/settings", label: "Settings", icon: "settings", perm: "settings.admin" },
    ],
  },
];

function initials(name: string): string {
  return (
    name
      .trim()
      .split(/\s+/)
      .map((p) => p[0]?.toUpperCase() ?? "")
      .slice(0, 2)
      .join("") || "?"
  );
}

export function Layout() {
  const navigate = useNavigate();
  const location = useLocation();
  const user = getCurrentUser();

  // Apply the tenant's saved branding colours app-wide as CSS variables.
  useEffect(() => {
    let cancelled = false;
    getBranding()
      .then((r) => {
        if (!cancelled) applyBranding(r.branding);
      })
      .catch(() => {
        /* keep defaults */
      });
    return () => {
      cancelled = true;
    };
  }, []);
  // Self-service profile photo: local mirror of the avatar so the appbar updates
  // immediately on save (the session user is updated too, for persistence).
  const [avatarUrl, setAvatarUrl] = useState<string | null>(user?.avatar_url ?? null);
  // Set once the user saves a photo from this shell — see the hydration effect below.
  const avatarSavedLocally = useRef(false);
  const [profileOpen, setProfileOpen] = useState(false);
  // The workspace display name (Settings → General) overrides the host-derived
  // name across the shell + browser tab; falls back to the host name until it
  // loads or when unset.
  const wsApi = useApi((signal) => getWorkspaceConfig(signal), []);
  const org = wsApi.data?.config.name?.trim() || orgFromHost();
  useEffect(() => {
    document.title = org === BRAND ? BRAND : `${org} · ${BRAND}`;
  }, [org]);
  // Mobile nav drawer: closed by default, opened by the appbar hamburger.
  // Auto-close whenever the route changes so a tap-through dismisses it.
  const [navOpen, setNavOpen] = useState(false);
  useEffect(() => setNavOpen(false), [location.pathname]);

  // Desktop-only: collapse the sidebar to icons-only. Personal, per-device
  // preference, so it lives in localStorage; ignored on mobile (the sidebar is a
  // drawer there) via CSS that scopes the collapsed styles to wide viewports.
  const [navCollapsed, setNavCollapsed] = useState(() => localStorage.getItem("ac.nav.collapsed") === "1");
  useEffect(() => {
    localStorage.setItem("ac.nav.collapsed", navCollapsed ? "1" : "0");
  }, [navCollapsed]);

  // Resolve the signed-in principal's permissions once per shell mount; until
  // they load (or if the probe fails) fall back to the full nav so staff are
  // never locked out of modules by a transient error.
  const meApi = useApi((signal) => getMe(undefined, signal), []);
  const perms = meApi.data?.permissions;
  const isAdmin = meApi.data?.is_admin === true || meApi.data?.roles?.includes("admin") === true;
  const planFeatures = meApi.data?.plan?.features;
  // Hydrate the appbar avatar from the fresh /api/auth/me — the cached session
  // can predate a profile-photo change (e.g. set by an admin), so trust the
  // server's current value and write it back to the session.
  //
  // Unless the user saved one HERE first: /api/auth/me is fetched once at mount
  // and can land after a save on a slow connection, in which case its (now
  // stale) value would revert the appbar and overwrite the session.
  useEffect(() => {
    if (avatarSavedLocally.current) return;
    const fresh = meApi.data?.user?.avatar_url;
    if (fresh === undefined) return;
    setAvatarUrl(fresh ?? null);
    const s = getSession();
    if (s && (s.user.avatar_url ?? null) !== (fresh ?? null)) {
      setSession({ ...s, user: { ...s.user, avatar_url: fresh ?? null } });
    }
  }, [meApi.data]);
  // Maintenance mode: non-admins get a full-screen notice instead of any module;
  // admins keep access (and see a banner) so they can turn it back off.
  const maintenanceOn = meApi.data?.maintenance?.enabled === true;
  const maintenanceMessage =
    meApi.data?.maintenance?.message?.trim() ||
    "We're performing scheduled maintenance and will be back shortly. Thanks for your patience.";
  // A nav item shows when the user has the permission AND (if the item is plan-
  // gated) the plan includes the feature. Unknown plan (no data yet) = show, so
  // a transient probe failure never hides modules.
  const featureOk = (item: NavItem) => !item.feature || !planFeatures || planFeatures[item.feature] === true;
  const canSee = (item: NavItem) =>
    featureOk(item) && (isAdmin || !perms || !item.perm || perms.includes(item.perm));
  // Drop items the user can't see, then drop any section left empty. While
  // maintenance mode is on, non-admins get NO module links — the maintenance
  // notice is the only thing they should see (admins keep the full nav).
  const sections =
    maintenanceOn && !isAdmin
      ? []
      : NAV_SECTIONS.map((s) => ({ title: s.title, items: s.items.filter(canSee) })).filter(
          (s) => s.items.length > 0,
        );

  // Highlight only the SINGLE best-matching nav item. A plain NavLink treats its
  // `to` as active for any descendant path, so "/assets" would also light up on
  // "/assets/assignments". Instead we pick the LONGEST `to` that matches the
  // current path: on "/assets/assignments" only Assignments wins; on an asset
  // detail "/assets/AC-0006" (no longer match) Inventory stays highlighted.
  const activeTo =
    sections
      .flatMap((s) => s.items.map((i) => i.to))
      .filter((to) => location.pathname === to || location.pathname.startsWith(`${to}/`))
      .sort((a, b) => b.length - a.length)[0] ?? null;

  async function logout() {
    // Best-effort server logout; clear local session regardless.
    try {
      await apiLogout();
    } catch {
      /* ignore — local clear is what matters */
    }
    clearSession();
    navigate("/login", { replace: true });
  }

  // Maintenance mode (non-admins): show ONLY the notice — no sidebar, no appbar,
  // no module routes. A sign-out control keeps a locked-out member from being
  // stranded. Admins fall through to the normal shell (with a banner).
  if (maintenanceOn && !isAdmin) {
    return (
      <div className="app-shell maint-locked">
        <StatusColorStyles />
        <div className="maint-screen" role="status">
          <div className="maint-screen-icon" aria-hidden="true">🛠️</div>
          <h1>Down for maintenance</h1>
          <p>{maintenanceMessage}</p>
          <button className="btn small maint-signout" onClick={logout}>
            <Icon name="logout" /> Sign out
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`app-shell${navOpen ? " nav-open" : ""}${navCollapsed ? " nav-collapsed" : ""}`}>
      {/* Native-app-style pull-to-refresh (mobile only). */}
      <PullToRefresh />
      {/* Tenant-configured status colors → injected pill overrides, app-wide. */}
      <StatusColorStyles />
      {/* Mobile-only scrim behind the drawer. */}
      <div className="nav-scrim" onClick={() => setNavOpen(false)} aria-hidden="true" />
      <aside className="sidebar">
        <div className="brand">
          <Logo size={30} />
          <span>
            {org}
            {/* The subtitle is the product brand; hide it when the workspace name
                already IS the brand (dev hosts) so it reads as just "ActionConnect". */}
            {org !== BRAND ? <small>ActionConnect</small> : null}
          </span>
        </div>
        <nav aria-label="Primary">
          {sections.map((section) => (
            <div className="nav-section" key={section.title}>
              <div className="nav-section-title">{section.title}</div>
              {section.items.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  // Function form: return the COMPLETE class so React Router does
                  // not also auto-append its own prefix-matched "active" (which
                  // would light up "/assets" on "/assets/assignments"). Our
                  // single-best-match `activeTo` is the only source of "active".
                  className={() => `nav-link${item.to === activeTo ? " active" : ""}`}
                  title={item.label}
                >
                  <span className="nav-icon" aria-hidden="true">
                    <Icon name={item.icon} />
                  </span>
                  <span className="nav-label">{item.label}</span>
                </NavLink>
              ))}
            </div>
          ))}
        </nav>
        <div className="spacer" />
        <div className="who">
          Signed in as
          <br />
          <strong>{user?.name || user?.email || "Agent"}</strong>
        </div>
      </aside>

      <div className="main">
        <header className="appbar">
          <button
            className="nav-toggle"
            aria-label="Open navigation"
            aria-expanded={navOpen}
            onClick={() => setNavOpen((v) => !v)}
          >
            <Icon name="menu" />
          </button>
          {/* Desktop-only: collapse the sidebar to icons (hidden on mobile, where
              the sidebar is a drawer toggled by .nav-toggle above). */}
          <button
            className="nav-collapse-btn"
            type="button"
            aria-label={navCollapsed ? "Expand sidebar" : "Collapse sidebar"}
            aria-pressed={navCollapsed}
            title={navCollapsed ? "Expand sidebar" : "Collapse sidebar"}
            onClick={() => setNavCollapsed((v) => !v)}
          >
            <Icon name="menu" />
          </button>
          {/* The workspace/org name lives in the sidebar brand; keep the appbar
              focused on search + actions. */}
          <GlobalSearch />
          <div className="appbar-actions">
            <NotificationsBell />
            <ThemeToggle />
            <button
              type="button"
              className="avatar-btn"
              onClick={() => setProfileOpen(true)}
              title="Edit your profile photo"
              aria-label="Edit your profile photo"
            >
              {avatarUrl ? (
                <img className="avatar avatar-img" src={avatarUrl} alt="" />
              ) : (
                <div className="avatar" aria-hidden="true">
                  {initials(user?.name || user?.email || "?")}
                </div>
              )}
            </button>
            <button className="btn small" onClick={logout}>
              <Icon name="logout" /> Sign out
            </button>
          </div>
        </header>
        <TicketNumberingProvider>
          <FacilityNumberingProvider>
           <ChangeNumberingProvider>
            {maintenanceOn ? (
              // Admins keep access during maintenance — remind them it's on and
              // align the banner with the page content below it.
              <div className="content maint-banner-wrap">
                <div className="maint-banner" role="status">
                  <span aria-hidden="true">🛠️</span>
                  <span>
                    <strong>Maintenance mode is ON.</strong> Members see a maintenance notice instead of the app.
                    You have access as an administrator — turn it off in <strong>Settings → General</strong>.
                  </span>
                </div>
              </div>
            ) : null}
            <Outlet />
           </ChangeNumberingProvider>
          </FacilityNumberingProvider>
        </TicketNumberingProvider>
      </div>

      {profileOpen ? (
        <ProfilePhotoModal
          current={avatarUrl}
          name={user?.name ?? ""}
          email={user?.email ?? ""}
          roles={meApi.data?.roles ?? []}
          groups={meApi.data?.groups ?? []}
          canUseTemplates={isAdmin || !!perms?.includes("ticket.write")}
          onClose={() => setProfileOpen(false)}
          onSaved={(url) => {
            avatarSavedLocally.current = true; // this beats a late /api/auth/me
            setAvatarUrl(url);
            // Persist onto the cached session so the avatar survives reloads.
            const s = getSession();
            if (s) setSession({ ...s, user: { ...s.user, avatar_url: url } });
            setProfileOpen(false);
          }}
        />
      ) : null}
    </div>
  );
}

/** Self-service profile photo editor — paste an image URL (preview live) and Save
 *  to persist via PATCH /api/auth/me. Clear the field and Save to fall back to
 *  initials. */
function ProfilePhotoModal({
  current,
  name,
  email,
  roles,
  groups,
  canUseTemplates,
  onClose,
  onSaved,
}: {
  current: string | null;
  name: string;
  email: string;
  roles: string[];
  groups: string[];
  /** Ticket templates are a helpdesk-agent tool — hide the section otherwise. */
  canUseTemplates: boolean;
  onClose: () => void;
  onSaved: (url: string | null) => void;
}) {
  const [url, setUrl] = useState(current ?? "");
  const { run, pending, error } = useMutation((next: string | null) => updateMyProfile({ avatar_url: next }));
  const trimmed = url.trim();
  const valid = trimmed === "" || /^https?:\/\//i.test(trimmed);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!valid) return;
    const res = await run(trimmed || null);
    if (res) onSaved(res.user?.avatar_url ?? null);
  }

  const roleLabel = roles.length
    ? roles.map((r) => r.charAt(0).toUpperCase() + r.slice(1)).join(", ")
    : "—";
  const groupLabel = groups.length ? groups.join(", ") : "—";

  return (
    <Modal
      title="Profile"
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose} disabled={pending}>
            Cancel
          </button>
          <button className="btn primary" type="submit" form="profile-photo-form" disabled={pending || !valid}>
            {pending ? <Spinner label="Saving…" /> : "Save"}
          </button>
        </>
      }
    >
      {error ? <Alert kind="error">{error}</Alert> : null}
      <div style={{ display: "flex", gap: 16, alignItems: "center", marginBottom: 14 }}>
        {trimmed && valid ? (
          <img className="avatar avatar-img" style={{ width: 56, height: 56 }} src={trimmed} alt="" />
        ) : (
          <div className="avatar" style={{ width: 56, height: 56, fontSize: "1.1rem" }} aria-hidden="true">
            {initials(name || email || "?")}
          </div>
        )}
        <span className="muted">Preview</span>
      </div>

      {/* Read-only account details (managed by an admin under People & access). */}
      <dl
        style={{
          display: "grid",
          gridTemplateColumns: "auto 1fr",
          gap: "7px 16px",
          margin: "0 0 16px",
          fontSize: "0.9rem",
        }}
      >
        <dt className="muted">Name</dt>
        <dd style={{ margin: 0 }}>{name || "—"}</dd>
        <dt className="muted">Email</dt>
        <dd style={{ margin: 0, wordBreak: "break-all" }}>{email || "—"}</dd>
        <dt className="muted">Role</dt>
        <dd style={{ margin: 0 }}>{roleLabel}</dd>
        <dt className="muted">Groups</dt>
        <dd style={{ margin: 0 }}>{groupLabel}</dd>
      </dl>

      <form id="profile-photo-form" onSubmit={onSubmit}>
        <label className="field">
          <span className="label">Photo URL</span>
          <input
            type="url"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://…/photo.jpg"
            autoFocus
          />
          <span className="hint">Paste a link to an image (https). Leave blank to use your initials.</span>
        </label>
      </form>

      {canUseTemplates ? <MyTicketTemplates /> : null}
    </Modal>
  );
}

/** Manage the ticket templates the signed-in user owns — rename, share (make
 *  usable by any agent on create), or delete. Templates shared by OTHER users
 *  aren't listed here (only their owner can manage them). */
function MyTicketTemplates() {
  const me = getCurrentUser();
  const { data, loading, error, reload } = useApi((s) => listTicketTemplates(s), []);
  const all = data?.templates ?? [];
  const mine = all.filter((t) => t.created_by && t.created_by === me?.id);
  // Shared by OTHER people — read-only here (only their owner can manage them).
  const sharedByOthers = all.filter((t) => t.is_shared === 1 && t.created_by !== me?.id);

  return (
    <div style={{ borderTop: "1px solid var(--border)", marginTop: 16, paddingTop: 14 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
        <strong>My ticket templates</strong>
        <span className="faint">{mine.length}</span>
      </div>
      {loading ? (
        <Spinner label="Loading…" />
      ) : error ? (
        <Alert kind="error">Could not load your templates.</Alert>
      ) : (
        <>
          {mine.length === 0 ? (
            <p className="hint" style={{ margin: 0 }}>
              None yet. Open a ticket and use “Save as Template” to create one.
            </p>
          ) : (
            <ul
              style={{
                listStyle: "none",
                margin: 0,
                padding: 0,
                display: "flex",
                flexDirection: "column",
                gap: 8,
                maxHeight: 220,
                overflowY: "auto",
              }}
            >
              {mine.map((t) => (
                <TemplateRow key={t.id} template={t} onChanged={reload} />
              ))}
            </ul>
          )}

          {sharedByOthers.length > 0 ? (
            <>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  margin: "14px 0 8px",
                }}
              >
                <strong>Shared by others</strong>
                <span className="faint">{sharedByOthers.length}</span>
              </div>
              <ul
                style={{
                  listStyle: "none",
                  margin: 0,
                  padding: 0,
                  display: "flex",
                  flexDirection: "column",
                  gap: 6,
                  maxHeight: 180,
                  overflowY: "auto",
                }}
              >
                {sharedByOthers.map((t) => (
                  <li key={t.id} className="person-row" style={{ gap: 8 }}>
                    <span className="person-main" style={{ minWidth: 0 }}>
                      {t.name}
                      {t.created_by_name ? <span className="faint"> · by {t.created_by_name}</span> : null}
                    </span>
                    <span className="pill" style={{ flex: "0 0 auto" }}>Shared</span>
                  </li>
                ))}
              </ul>
              <p className="hint" style={{ marginTop: 6 }}>
                These are available to everyone when creating a ticket. Only their owner can change them.
              </p>
            </>
          ) : null}
        </>
      )}
    </div>
  );
}

function TemplateRow({ template, onChanged }: { template: TicketTemplate; onChanged: () => void }) {
  // Name renders as plain text until the pencil switches it to an input.
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(template.name);
  const [confirming, setConfirming] = useState(false);
  const saveMut = useMutation((patch: { name?: string; is_shared?: boolean }) =>
    updateTicketTemplate(template.id, patch),
  );
  const delMut = useMutation(() => deleteTicketTemplate(template.id));
  const busy = saveMut.pending || delMut.pending;
  const shared = template.is_shared === 1;

  async function commitName() {
    setEditing(false);
    const n = name.trim();
    if (!n || n === template.name) {
      setName(template.name);
      return;
    }
    const res = await saveMut.run({ name: n });
    if (res) onChanged();
    else setName(template.name);
  }

  return (
    <li className="person-row" style={{ gap: 8 }}>
      {editing ? (
        <input
          className="person-main"
          style={{ minWidth: 0, flex: 1 }}
          value={name}
          onChange={(e) => setName(e.target.value)}
          onBlur={commitName}
          onKeyDown={(e) => {
            if (e.key === "Enter") (e.currentTarget as HTMLInputElement).blur();
            if (e.key === "Escape") {
              setName(template.name);
              setEditing(false);
            }
          }}
          disabled={busy}
          autoFocus
          aria-label="Template name"
        />
      ) : (
        <>
          <span
            className="person-main"
            style={{ minWidth: 0, flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
            title={template.name}
          >
            {template.name}
          </span>
          <button
            type="button"
            className="btn small"
            style={{ flex: "0 0 auto" }}
            disabled={busy}
            onClick={() => {
              setName(template.name);
              setEditing(true);
            }}
            aria-label={`Rename ${template.name}`}
            title="Rename"
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z" />
            </svg>
          </button>
        </>
      )}
      <label className="toggle" title={shared ? "Shared with all agents" : "Private to you"} style={{ flex: "0 0 auto" }}>
        <input
          type="checkbox"
          checked={shared}
          disabled={busy}
          onChange={async (e) => {
            const res = await saveMut.run({ is_shared: e.target.checked });
            if (res) onChanged();
          }}
        />
        Shared
      </label>
      {confirming ? (
        <button
          type="button"
          className="btn small danger"
          style={{ flex: "0 0 auto" }}
          disabled={busy}
          onClick={async () => {
            const res = await delMut.run();
            if (res) onChanged();
          }}
        >
          {delMut.pending ? "…" : "Confirm"}
        </button>
      ) : (
        <button
          type="button"
          className="btn small"
          style={{ flex: "0 0 auto" }}
          disabled={busy}
          onClick={() => setConfirming(true)}
          onBlur={() => setConfirming(false)}
          aria-label={`Delete ${template.name}`}
        >
          Delete
        </button>
      )}
    </li>
  );
}

/** Page-level top bar for breadcrumbs + page actions (rendered inside content).
 *  The leading icon is resolved from the current route by matching the nav items
 *  (longest `to` prefix wins, same rule as the sidebar highlight), so every page
 *  shows its module icon without each page passing one. An explicit `icon`
 *  overrides the auto match; `icon={null}` suppresses it. */
export function PageBar({
  crumbs,
  actions,
  icon,
}: {
  crumbs: ReactNode;
  actions?: ReactNode;
  icon?: IconName | null;
}) {
  const { pathname } = useLocation();
  const autoIcon =
    NAV_SECTIONS.flatMap((s) => s.items)
      .filter((i) => pathname === i.to || pathname.startsWith(`${i.to}/`))
      .sort((a, b) => b.to.length - a.to.length)[0]?.icon ?? null;
  const shown = icon === undefined ? autoIcon : icon;
  return (
    <header className="topbar">
      <div className="crumbs">
        {shown ? <Icon name={shown} className="crumb-icon" /> : null}
        {crumbs}
      </div>
      <div className="btn-row">{actions}</div>
    </header>
  );
}
